import type { ICellRendererParams } from "ag-grid-community";
import { ExternalLinkIcon } from "@heroicons/react/outline";

import { usePromptLayerToolLink } from "./hooks/usePromptLayerToolLink";
import { SpanHoverCard } from "./SpanHoverCard";
import { SpanIcon } from "./SpanIcon";
import { getSpanDisplayName } from "./utils";

import { useUser } from "@/context/user-context";
import { SpanNode } from "@/types/spans";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { keepRelevantSearchParams } from "@/utils/utils";

interface SpanTableRow {
  span_id: string;
  name: string;
  end: string | null;
  iteration?: number;
  parent_id: string | null;
  status: string;
  type: string;
  duration: number;
  [key: string]: unknown;
}

export const NameInnerRenderer = (params: ICellRendererParams<SpanTableRow>) => {
  const { data } = params;
  const user = useUser();
  const spanNode = data ? (data as unknown as SpanNode) : null;
  const toolLink = usePromptLayerToolLink(spanNode);

  if (!data || !spanNode) {
    return null;
  }

  const displayName = getSpanDisplayName(spanNode);
  const requestLogId = spanNode.request_log_id;
  const workspaceId = user?.activeWorkspaceId;

  const handleOpenRequestLog = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (requestLogId && workspaceId) {
      const qs = keepRelevantSearchParams(new URLSearchParams(window.location.search));
      const suffix = qs ? `?${qs}` : "";
      window.open(
        `/workspace/${workspaceId}/request/${requestLogId}${suffix}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  const handleOpenTool = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (toolLink) {
      window.open(toolLink.url, "_blank", "noopener,noreferrer");
    }
  };

  const isRootSpan = spanNode.parent_id === null;
  const isEditorRun = spanNode.attributes?.is_editor_run === true;
  const isTestRun = spanNode.attributes?.is_test_run === "true";
  const iteration = data.iteration !== undefined ? data.iteration : null;

  return (
    <BasicTooltip
      content={
        <SpanHoverCard
          spanNode={spanNode}
          status={data.status}
          typeValue={data.type}
          durationMs={data.duration}
          isPromptLayerTool={Boolean(toolLink)}
        />
      }
      variant="white"
      side="left"
      delayDuration={400}
    >
      <div className="flex h-full w-full items-center gap-2">
        <SpanIcon spanNode={spanNode} isPromptLayerTool={Boolean(toolLink)} />
        <div className="relative flex min-w-0 items-center gap-1.5">
          <span className="w-full min-w-0 truncate font-medium text-gray-900">{displayName}</span>
          {isRootSpan && (isEditorRun || isTestRun) && (
            <BasicTooltip content={isEditorRun ? "Editor Run" : "Test Run"}>
              <span className="absolute -right-2 -top-1 font-semibold text-yellow-600">*</span>
            </BasicTooltip>
          )}
          {iteration !== null && (
            <span className="text-xs font-normal text-gray-400">[{iteration}]</span>
          )}
        </div>
        {!isRootSpan && requestLogId && spanNode.request_log_meta?.model && (
          <span className="max-w-[140px] shrink-0 truncate rounded-full bg-gray-100 px-1.5 py-px text-[10px] font-medium leading-4 text-gray-600">
            {spanNode.request_log_meta.model}
          </span>
        )}
        {requestLogId && workspaceId && (
          <BasicTooltip content="Open Request Log">
            <Button
              onClick={handleOpenRequestLog}
              size="tinyIcon"
              variant="ghost"
              className="h-5 w-5 p-0"
            >
              <ExternalLinkIcon className="h-3 w-3" />
            </Button>
          </BasicTooltip>
        )}
        {toolLink && (
          <BasicTooltip content={`Open PromptLayer tool: ${toolLink.toolName}`}>
            <Button
              onClick={handleOpenTool}
              size="tinyIcon"
              variant="ghost"
              className="h-5 w-5 p-0"
            >
              <ExternalLinkIcon className="h-3 w-3" />
            </Button>
          </BasicTooltip>
        )}
      </div>
    </BasicTooltip>
  );
};
